/**
 * EntityLink — a link to a Company / Person / Spin-out candidate detail page.
 *
 * Default: same-tab SPA navigation via TanStack Router's <Link>. The list
 * page the user came from keeps its state because its filters/sort/pagination
 * live in URL search params — browser-back restores that page verbatim.
 *
 * Opt in to new-tab navigation explicitly with `newTab`. That falls back to a
 * plain <a target="_blank"> — useful for "open in new tab" links the user has
 * triggered themselves (rare).
 */
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { normalizeCompanyName, normalizePersonName } from '@/utils/displayName';
import styles from './EntityLink.module.css';

export type EntityKind = 'company' | 'person' | 'spinout';

export interface EntityLinkProps {
  kind: EntityKind;
  id: string;
  name?: string | null;
  /**
   * Backend-computed human-readable name (`display_name`), when available.
   * Used verbatim instead of re-normalising `name` client-side — the
   * backend algorithm is authoritative (legal-suffix stripping, deliberate
   * mixed-case preservation like "JPMorgan") and re-running the client-side
   * title-case heuristic on top of it would undo that work.
   */
  displayName?: string | null;
  /** Optional secondary line below the name (e.g. reg number · jurisdiction). */
  subtitle?: ReactNode;
  /** Default false (same-tab SPA nav). Set true to open in a new browser tab. */
  newTab?: boolean;
  /**
   * Override the default destination derived from `kind`. Useful on Radar, where
   * the person cell should route to the spin-out candidate detail (radar-
   * context) rather than the persona page (database-context). The `kind` still
   * drives name normalisation.
   */
  to?: string;
  className?: string;
}

const PATHS: Record<EntityKind, (id: string) => string> = {
  company: (id) => `/database/companies/${id}`,
  person: (id) => `/database/people/${id}`,
  spinout: (id) => `/radar/${id}`,
};

export function EntityLink({
  kind,
  id,
  name,
  displayName,
  subtitle,
  newTab = false,
  to: overrideTo,
  className,
}: EntityLinkProps) {
  const display = displayName?.trim()
    ? displayName
    : kind === 'person' ? normalizePersonName(name) : normalizeCompanyName(name);
  const to = overrideTo ?? PATHS[kind](id);
  const mergedClass = `${styles.link} ${className ?? ''}`;

  if (newTab) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={mergedClass}
      >
        <span className={styles.name}>{display || '—'}</span>
        {subtitle != null && <span className={styles.subtitle}>{subtitle}</span>}
      </a>
    );
  }

  return (
    <Link to={to} className={mergedClass}>
      <span className={styles.name}>{display || '—'}</span>
      {subtitle != null && <span className={styles.subtitle}>{subtitle}</span>}
    </Link>
  );
}
